/**
 * A task describes itself (product owner, 2026-10-18): what its answers are
 * called, their types and the order they were asked in.
 *
 * The JavaScript copy of `task_field_snapshot` in
 * api/app/utils/task_field_snapshot.py (byte-identical in the functions).
 * All three run the cases in api/tests/fixtures/task_field_snapshot_cases.json
 * (taskFieldSnapshot.test.js), so they cannot drift: change them together.
 *
 * Labels are the template's SOURCE language, whatever language the operator
 * read the form in. The template is read only for what the task does not
 * carry. Precedence, first that applies:
 *
 * 1. `field_snapshot` (phone Build 74+, the public QR page, migrated SSS
 *    tasks): its labels, types and order (`seq`). source "snapshot".
 * 2. Four-part headers without `capture_locale`: the header labels, types and
 *    order (`fieldSequence`, then the header sequence). source "headers".
 * 3. Four-part headers with `capture_locale`: order and types from the
 *    headers, labels from the template by field id, the header where the
 *    template has none. source "headers".
 * 4. Otherwise (two-part headers before Build 57, API-created tasks, migrated
 *    tasks keyed by label): the template's labels, types and order, a
 *    label-keyed answer matched to its field by label, `fieldSequence` first.
 *    source "template".
 *
 * Whatever none of those placed comes last, alphabetically by key, named by
 * the header (the template first for a translated capture), else the key.
 * source "answers". Underscore keys are the phone's bookkeeping and are left
 * out. Only answered keys are listed (an empty answer is still an answer).
 */

const ANSWER_KEYS = ['keyValueMap', 'formData', 'formResponses', 'form_data', 'form_responses', 'data'];

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** The map holding a task's answers, `{}` if none. */
export function formAnswers(task) {
  for (const key of ANSWER_KEYS) {
    const candidate = task?.[key];
    if (isPlainObject(candidate) && Object.keys(candidate).length > 0) return candidate;
  }
  return {};
}

const text = (value) => (typeof value === 'string' ? value.trim() : '');

const labelNorm = (value) => String(value ?? '').trim().split(/\s+/).filter(Boolean).join(' ').toLowerCase();

// Python's `int(x)`, as far as a sort order can be: an integer stays, a float
// truncates, a string of digits parses, anything else is 0.
function sortOrder(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
  if (typeof raw === 'string' && /^\s*[+-]?\d+\s*$/.test(raw)) return parseInt(raw, 10);
  return 0;
}

/** `sequence:fieldId:name:type` (or legacy `fieldId:name`) -> Map in header order. */
export function parseHeaders(headers) {
  const parsed = new Map();
  if (!Array.isArray(headers)) return parsed;
  headers.forEach((header) => {
    if (typeof header !== 'string') return;
    const parts = header.split(':');
    if (parts.length >= 4) {
      const [sequence, fieldId, name, type] = parts;
      const seq = /^\s*[+-]?\d+\s*$/.test(sequence) ? parseInt(sequence, 10) : 999;
      parsed.set(fieldId, { name, sequence: seq, type });
    } else if (parts.length === 2) {
      parsed.set(parts[0], { name: parts[1], sequence: 999 });
    }
  });
  return parsed;
}

function templateIndex(templateFields) {
  const rows = [];
  const labels = new Map();
  const types = new Map();
  const byLabel = new Map();
  const seen = new Set();
  (Array.isArray(templateFields) ? templateFields : []).forEach((field, index) => {
    if (!isPlainObject(field)) return;
    const fid = field.id || field.field_id;
    if (typeof fid !== 'string' || !fid || seen.has(fid)) return;
    seen.add(fid);
    rows.push({ sortOrder: sortOrder(field.sort_order ?? 0), index, fid });
    const label = text(field.label) || text(field.name);
    if (label) {
      labels.set(fid, label);
      const norm = labelNorm(label);
      if (!byLabel.has(norm)) byLabel.set(norm, fid);
    }
    const type = field.type || field.field_type;
    if (typeof type === 'string' && type) types.set(fid, type);
  });
  rows.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.index - b.index));
  return { order: rows.map((r) => r.fid), labels, types, byLabel };
}

// Code-unit order, like Python's `sorted` on strings for everything a key holds.
const byKey = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * @param {Object} task
 * @param {Array} [templateFields] the template the task was filled on (its
 *   recorded version when there is one), raw `fields`
 * @returns {Array<{key, label, type, source, fieldId, shownLabel}>}
 */
export function taskFieldSnapshot(task, templateFields = null) {
  const answers = formAnswers(task);
  const keys = Object.keys(answers).filter((k) => !k.startsWith('_'));
  if (keys.length === 0) return [];
  const answerSet = new Set(keys);

  const { order, labels: tLabels, types: tTypes, byLabel } = templateIndex(templateFields);
  const tIds = new Set(order);
  const parsed = parseHeaders(task?.headers);
  const fullHeaders = [...parsed.values()].some((info) => 'type' in info);
  const capture = task?.capture_locale;
  const translated = typeof capture === 'string' && capture.trim() !== '';
  let sequence = task?.fieldSequence || task?.field_sequence || [];
  if (!Array.isArray(sequence)) sequence = [];

  const fieldOf = (key) => (tIds.has(key) ? key : (byLabel.get(labelNorm(key)) ?? null));
  const headerName = (key) => text(parsed.get(key)?.name);
  const headerType = (key) => text(parsed.get(key)?.type);
  const templateLabel = (fid) => (fid ? tLabels.get(fid) || '' : '');
  const templateType = (fid) => (fid ? tTypes.get(fid) || '' : '');
  const fallbackLabel = (key, fid) => (translated
    ? (templateLabel(fid) || headerName(key) || key)
    : (headerName(key) || templateLabel(fid) || key));

  const out = [];
  const placed = new Set();
  const add = (key, label, type, source, fieldId, shown = '') => {
    placed.add(key);
    out.push({
      key, label, type, source, fieldId,
      shownLabel: shown && shown !== label ? shown : null,
    });
  };

  const snapRows = [];
  if (Array.isArray(task?.field_snapshot)) {
    task.field_snapshot.forEach((entry, index) => {
      if (!isPlainObject(entry)) return;
      const key = entry.id;
      if (typeof key !== 'string' || !key) return;
      const seq = typeof entry.seq === 'number' && Number.isFinite(entry.seq) ? entry.seq : Infinity;
      snapRows.push({ seq, index, key, entry });
    });
  }

  if (snapRows.length > 0) {
    snapRows.sort((a, b) => (a.seq === b.seq ? a.index - b.index : (a.seq < b.seq ? -1 : 1)));
    snapRows.forEach(({ key, entry }) => {
      if (!answerSet.has(key) || placed.has(key)) return;
      const fid = fieldOf(key);
      const shown = text(entry.shown_label);
      const label = text(entry.label) || templateLabel(fid) || shown || headerName(key) || key;
      const type = text(entry.type) || templateType(fid) || headerType(key);
      add(key, label, type, 'snapshot', fid, shown);
    });
  } else if (fullHeaders) {
    const ordered = sequence.filter((k) => typeof k === 'string' && answerSet.has(k));
    const rest = [...parsed.keys()].filter((k) => answerSet.has(k) && !ordered.includes(k));
    rest.sort((a, b) => (parsed.get(a).sequence ?? 999) - (parsed.get(b).sequence ?? 999));
    [...ordered, ...rest].forEach((key) => {
      if (placed.has(key)) return;
      const fid = fieldOf(key);
      add(key, fallbackLabel(key, fid), headerType(key) || templateType(fid), 'headers', fid, headerName(key));
    });
  } else {
    const ordered = sequence.filter((k) => typeof k === 'string' && answerSet.has(k));
    const sortedKeys = [...keys].sort(byKey);
    order.forEach((fid) => {
      if (answerSet.has(fid)) {
        ordered.push(fid);
        return;
      }
      const norm = labelNorm(tLabels.get(fid) || '');
      if (!norm) return;
      const match = sortedKeys.find((key) => !ordered.includes(key) && !tIds.has(key) && labelNorm(key) === norm);
      if (match !== undefined) ordered.push(match);
    });
    ordered.forEach((key) => {
      if (placed.has(key)) return;
      const fid = fieldOf(key);
      add(key, templateLabel(fid) || headerName(key) || key, templateType(fid) || headerType(key), 'template', fid);
    });
  }

  keys.filter((k) => !placed.has(k)).sort(byKey).forEach((key) => {
    const fid = fieldOf(key);
    add(key, fallbackLabel(key, fid), headerType(key) || templateType(fid), 'answers', fid,
      translated ? headerName(key) : '');
  });
  return out;
}

export default taskFieldSnapshot;
